import { forCloud } from '../tenancy/cloud-account.js';
import { azureLocationProblem } from './azure-run.js';
/**
 * Which region a deployment lives in.
 *
 * A region belongs to a *deployment*, not to a cloud account (IM-91): one
 * customer account can hold instances in several regions, and one region can
 * hold several deployments. Until this existed the region was a fallback in the
 * AWS adapter — `deps.region ?? 'us-east-1'` — which silently decided where a
 * customer's infrastructure went.
 */
/**
 * Whether a string is shaped like an AWS region, or why it is not.
 *
 * ## Why a shape and not an allow list
 *
 * The obvious alternative is a list of the regions AWS has. It is tempting
 * because it catches `us-esat-1`, which this does not. It is still the wrong
 * trade:
 *
 * - **It goes stale in the direction that hurts.** AWS adds regions; a list
 *   compiled today refuses a legitimate one next year, and refusing a customer's
 *   real region is worse than accepting a typo.
 * - **A typo fails fast and legibly anyway.** A wrong-but-well-formed region
 *   reaches `dispatcherArn` and the SDK either cannot resolve an endpoint or
 *   returns `ResourceNotFound` for a dispatcher that is not there. That is a
 *   prompt error naming the region — not a container that dies ninety seconds
 *   in, which is the failure mode worth spending validation on.
 * - **The real guard is elsewhere.** IM-95 and IM-96 offer only the regions an
 *   account is actually bootstrapped in, which rejects `us-esat-1` and every
 *   other region the customer has no state backend in. This function's job is
 *   to keep a malformed string out of an ARN, and that is all.
 *
 * The shape covers the partitions in use — `us-east-1`, `eu-west-2`,
 * `ap-southeast-3`, and the `-gov-` and `-iso-` forms — deliberately, since
 * refusing a GovCloud region for want of a hyphen would be the same failure as
 * a stale list.
 */
const REGION_SHAPE = /^[a-z]{2}(-[a-z]+)+-\d{1,2}$/;
/** The longest real region name is well under this; the cap is anti-nonsense. */
const MAX_REGION_LENGTH = 40;
export const regionProblem = (region) => {
    if (region === '')
        return 'A region is required.';
    if (region.length > MAX_REGION_LENGTH)
        return 'That is not a region.';
    if (region !== region.trim())
        return 'A region may not have leading or trailing spaces.';
    if (!REGION_SHAPE.test(region)) {
        // Names the shape rather than saying "invalid": the caller is usually one
        // character out, and an example is worth more than a rule.
        return `"${region}" is not shaped like a region. Expected something like us-east-1.`;
    }
    return undefined;
};
/**
 * `regionProblem` for the account's cloud: an AWS region, or an Azure
 * location (IM-46). Absent reads as AWS's, as `deploymentIdProblemFor` does.
 */
export const regionProblemFor = (cloud, region) => forCloud(cloud, { aws: () => regionProblem(region), azure: () => azureLocationProblem(region) }, () => regionProblem(region));
/**
 * The region a deployment already lives in, from its runs.
 *
 * The newest run that recorded one wins, which is the same rule
 * `deployments/deployment.ts` uses to derive a deployment's region for display —
 * deliberately, so the value a run is checked against is the value the console
 * shows. Runs recorded before IM-92 have none, and a deployment made entirely of
 * those returns `undefined`: unknown, not `us-east-1`.
 */
export const regionOfDeployment = (runs, deploymentId) => runs.find((run) => run.deploymentId === deploymentId && run.region !== undefined)?.region;
/**
 * Whether a run may target `region`, given the deployment it names.
 *
 * **This is the guard the whole story exists for.** A deployment's state lives
 * at `{deployment_id}/state.tfstate` in *that region's* bucket. A run naming an
 * existing deployment but pointed at a different region finds no state there,
 * so Terraform does not update the instance — it builds a **second** one. The
 * console still shows one deployment, now describing whichever run wrote last,
 * and every part of that reads as success.
 *
 * So an existing deployment's region is not a default to override. It is a
 * fact, and disagreeing with it is refused.
 *
 * A deployment whose runs predate IM-92 has no recorded region, and this
 * permits the run rather than guessing: the alternative is refusing every run
 * against every deployment that existed before the field did.
 */
export const regionConflict = (existing, requested) => {
    if (existing === undefined || existing === requested)
        return undefined;
    return (`This deployment is in ${existing}. A run in ${requested} would not find its ` +
        `state and would build a second instance, so it is refused. Start it in ${existing}, ` +
        `or use a new deployment id for a separate instance.`);
};
//# sourceMappingURL=regions.js.map